import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead,
  TableHeader, TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar,
} from "recharts";
import {
  Users, DollarSign, ShoppingBag, Target,
  TrendingUp, Lock, ArrowLeft, RefreshCw,
  Crown, Activity,
} from "lucide-react";
import { toast } from "sonner";

const ADMIN_KEY_STORAGE = "savecation_admin_key";

function StatCard({ title, value, sub, icon: Icon, color }) {
  return (
    <Card className="border-0 shadow-md" data-testid={`stat-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <CardContent className="p-5">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-slate-500 mb-1">{title}</p>
            <p className="text-2xl font-bold text-slate-900">{value}</p>
            {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
          </div>
          <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${color}`}>
            <Icon className="w-6 h-6" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function GrowthChart({ data }) {
  return (
    <Card className="border-0 shadow-md" data-testid="growth-chart">
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-sky-500" /> User Signups (30 days)
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data}>
              <defs>
                <linearGradient id="signupGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#0EA5E9" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#0EA5E9" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(v) => v.slice(5)} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip contentStyle={{ borderRadius: 12, border: "none", boxShadow: "0 4px 20px rgba(0,0,0,.1)" }} />
              <Area type="monotone" dataKey="signups" stroke="#0EA5E9" strokeWidth={2} fill="url(#signupGrad)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}

function RevenueChart({ data }) {
  const chartData = Object.entries(data).map(([key, val]) => ({
    name: key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
    total: val.total || 0,
    count: val.count || 0,
  }));

  if (!chartData.length) {
    return (
      <Card className="border-0 shadow-md">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-emerald-500" /> Revenue by Type
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-400 text-center py-8">No revenue data yet</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-0 shadow-md" data-testid="revenue-chart">
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          <DollarSign className="w-4 h-4 text-emerald-500" /> Revenue by Type
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip contentStyle={{ borderRadius: 12, border: "none", boxShadow: "0 4px 20px rgba(0,0,0,.1)" }} />
              <Bar dataKey="total" fill="#10B981" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}

function TransactionsTable({ transactions }) {
  if (!transactions.length) {
    return <p className="text-sm text-slate-400 text-center py-8">No transactions found</p>;
  }
  return (
    <div className="overflow-x-auto" data-testid="transactions-table">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Type</TableHead>
            <TableHead>Amount</TableHead>
            <TableHead>User</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Date</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {transactions.map((tx, i) => (
            <TableRow key={i}>
              <TableCell>
                <Badge variant="outline" className="capitalize">
                  {(tx.transaction_type || "unknown").replace(/_/g, " ")}
                </Badge>
              </TableCell>
              <TableCell className="font-semibold">${(tx.amount || 0).toFixed(2)}</TableCell>
              <TableCell className="text-slate-500 text-xs truncate max-w-[120px]">{tx.user_id || "-"}</TableCell>
              <TableCell>
                <span className={`inline-block w-2 h-2 rounded-full mr-1.5 ${tx.payment_status === "paid" ? "bg-emerald-400" : "bg-amber-400"}`} />
                {tx.payment_status || "pending"}
              </TableCell>
              <TableCell className="text-xs text-slate-400">{tx.created_at ? new Date(tx.created_at).toLocaleDateString() : "-"}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function UsersTable({ users }) {
  if (!users.length) {
    return <p className="text-sm text-slate-400 text-center py-8">No users found</p>;
  }
  return (
    <div className="overflow-x-auto" data-testid="users-table">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Tier</TableHead>
            <TableHead>Wallet</TableHead>
            <TableHead>Joined</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {users.map((u, i) => (
            <TableRow key={i}>
              <TableCell className="font-medium">{u.name || "Anonymous"}</TableCell>
              <TableCell className="text-slate-500 text-xs">{u.email || "-"}</TableCell>
              <TableCell>
                <Badge className={u.subscription_tier === "pro" ? "bg-amber-100 text-amber-700" : u.subscription_tier === "team" ? "bg-purple-100 text-purple-700" : "bg-slate-100 text-slate-600"}>
                  {u.subscription_tier === "pro" && <Crown className="w-3 h-3 mr-1" />}
                  {(u.subscription_tier || "free").toUpperCase()}
                </Badge>
              </TableCell>
              <TableCell className="font-semibold">${(u.wallet_balance || 0).toFixed(2)}</TableCell>
              <TableCell className="text-xs text-slate-400">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "-"}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function TopSellersTable({ sellers }) {
  if (!sellers.length) {
    return <p className="text-sm text-slate-400 text-center py-8">No marketplace activity yet</p>;
  }
  return (
    <div className="overflow-x-auto" data-testid="top-sellers-table">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>#</TableHead>
            <TableHead>Seller</TableHead>
            <TableHead>Sales</TableHead>
            <TableHead>Volume</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sellers.map((s, i) => (
            <TableRow key={i}>
              <TableCell className="font-bold text-sky-500">{i + 1}</TableCell>
              <TableCell className="font-medium">{s.seller_name || s._id || "Unknown"}</TableCell>
              <TableCell>{s.total_sales}</TableCell>
              <TableCell className="font-semibold">${(s.total_volume || 0).toFixed(2)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export default function Admin() {
  const navigate = useNavigate();
  const [adminKey, setAdminKey] = useState(sessionStorage.getItem(ADMIN_KEY_STORAGE) || "");
  const [authenticated, setAuthenticated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [overview, setOverview] = useState(null);
  const [growth, setGrowth] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [users, setUsers] = useState([]);
  const [topSellers, setTopSellers] = useState([]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    const headers = { "X-Admin-Key": adminKey };
    try {
      const [ovRes, grRes, txRes, usRes, tsRes] = await Promise.all([
        fetch(`${API}/admin/overview`, { headers }),
        fetch(`${API}/admin/growth`, { headers }),
        fetch(`${API}/admin/transactions?limit=20`, { headers }),
        fetch(`${API}/admin/users?limit=30`, { headers }),
        fetch(`${API}/admin/top-sellers`, { headers }),
      ]);

      if (!ovRes.ok) throw new Error("Unauthorized");

      setOverview(await ovRes.json());
      setGrowth(await grRes.json());
      setTransactions(await txRes.json());
      setUsers(await usRes.json());
      setTopSellers(await tsRes.json());
      setAuthenticated(true);
      sessionStorage.setItem(ADMIN_KEY_STORAGE, adminKey);
    } catch {
      toast.error("Invalid admin key or server error");
      setAuthenticated(false);
      localStorage.removeItem(ADMIN_KEY_STORAGE);
    } finally {
      setLoading(false);
    }
  }, [adminKey]);

  const handleLogin = (e) => {
    e.preventDefault();
    if (!adminKey.trim()) return;
    fetchAll();
  };

  useEffect(() => {
    if (adminKey) fetchAll();
  }, []);

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-6" data-testid="admin-login">
        <Card className="w-full max-w-sm border-slate-800 bg-slate-900 shadow-2xl">
          <CardHeader className="text-center">
            <div className="mx-auto w-14 h-14 rounded-2xl bg-sky-500/10 flex items-center justify-center mb-3">
              <Lock className="w-7 h-7 text-sky-400" />
            </div>
            <CardTitle className="text-xl text-white">Admin Dashboard</CardTitle>
            <p className="text-sm text-slate-400 mt-1">Enter your admin key to continue</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <Input
                type="password"
                placeholder="Admin Key"
                value={adminKey}
                onChange={(e) => setAdminKey(e.target.value)}
                className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                data-testid="admin-key-input"
              />
              <Button type="submit" className="w-full bg-sky-500 hover:bg-sky-600" disabled={loading} data-testid="admin-login-btn">
                {loading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Lock className="w-4 h-4 mr-2" />}
                {loading ? "Verifying..." : "Access Dashboard"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  const ov = overview || { users: {}, revenue: { marketplace_commission: {}, by_type: {} }, marketplace: {}, goals: {} };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      <header className="sticky top-0 z-30 bg-white border-b border-slate-200 px-4 sm:px-6 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/")} className="text-slate-400 hover:text-slate-700" data-testid="admin-back-btn">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2">
              <Activity className="w-5 h-5 text-sky-500" />
              <h1 className="text-lg font-bold text-slate-900">Savecation Admin</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={fetchAll} disabled={loading} data-testid="admin-refresh-btn">
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
            </Button>
            <Button size="sm" variant="ghost" onClick={() => { localStorage.removeItem(ADMIN_KEY_STORAGE); setAuthenticated(false); setAdminKey(""); }} data-testid="admin-logout-btn">
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard title="Total Users" value={ov.users.total || 0} sub={`+${ov.users.this_month || 0} this month`} icon={Users} color="bg-sky-100 text-sky-600" />
          <StatCard title="Pro Users" value={ov.users.pro || 0} icon={Crown} color="bg-amber-100 text-amber-600" />
          <StatCard title="Commission" value={`$${(ov.revenue.marketplace_commission?.total || 0).toFixed(2)}`} sub={`$${(ov.revenue.marketplace_commission?.this_month || 0).toFixed(2)} this month`} icon={DollarSign} color="bg-emerald-100 text-emerald-600" />
          <StatCard title="Marketplace" value={`${ov.marketplace.active || 0} active`} sub={`${ov.marketplace.sold || 0} sold`} icon={ShoppingBag} color="bg-violet-100 text-violet-600" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <GrowthChart data={growth} />
          <RevenueChart data={ov.revenue.by_type || {}} />
        </div>

        <Tabs defaultValue="transactions" className="w-full">
          <TabsList className="bg-white border border-slate-200 mb-4">
            <TabsTrigger value="transactions" data-testid="tab-transactions">Transactions</TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-users">Users</TabsTrigger>
            <TabsTrigger value="marketplace" data-testid="tab-marketplace">Top Sellers</TabsTrigger>
          </TabsList>

          <TabsContent value="transactions">
            <Card className="border-0 shadow-md">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Recent Transactions</CardTitle>
              </CardHeader>
              <CardContent><TransactionsTable transactions={transactions} /></CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="users">
            <Card className="border-0 shadow-md">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Recent Users</CardTitle>
              </CardHeader>
              <CardContent><UsersTable users={users} /></CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="marketplace">
            <Card className="border-0 shadow-md">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Top Marketplace Sellers</CardTitle>
              </CardHeader>
              <CardContent><TopSellersTable sellers={topSellers} /></CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <div className="grid grid-cols-2 gap-4">
          <StatCard title="Total Goals" value={ov.goals.total || 0} icon={Target} color="bg-rose-100 text-rose-600" />
          <StatCard title="Active Goals" value={ov.goals.active || 0} icon={Target} color="bg-teal-100 text-teal-600" />
        </div>

        <p className="text-center text-xs text-slate-400 pb-8">
          Generated at {overview?.generated_at ? new Date(overview.generated_at).toLocaleString() : "-"}
        </p>
      </main>
    </div>
  );
}
